import { useCallback, useEffect, useState } from 'react';
import { call, consoleApiFor as buildConsoleApi } from '@/api.ts';
import type { FragmentDraft, Shell } from '@/api.ts';
import type { Session } from '@/session.tsx';

export { baseUrlFor } from '@/api.ts';

/** Re-exported so pages import their data access from one place. */
export const consoleApiFor = buildConsoleApi;

interface Resource<T> {
  items: T | null;
  error: string | null;
  reload(): void;
}

/**
 * A load-on-mount resource with a manual reload.
 *
 * No cache and no client-side store: the console is an operator tool where "what does the server
 * say right now" is the only useful answer, and a stale registry shown confidently is worse than
 * a spinner.
 */
function useResource<T>(session: Session, path: string, select: (body: never) => T): Resource<T> {
  const [items, setItems] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    setError(null);
    call<never>(session, path)
      .then((body) => { if (live) setItems(select(body)); })
      .catch((cause: Error) => { if (live) setError(cause.message); });
    return () => { live = false; };
    // `select` is intentionally not a dependency: it is a literal at each call site, so a new
    // identity every render would make this reload forever.
  }, [session, path, nonce, select]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);
  return { items, error, reload };
}

/**
 * Module-level selectors, so their identity is stable across renders and `useResource` can list
 * `select` as an honest dependency instead of suppressing the rule.
 */
const selectItems = <T,>(body: never): T[] => (body as { items: T[] }).items;
const selectDrafts = (body: never): FragmentDraft[] => selectItems<FragmentDraft>(body);
const selectShells = (body: never): Shell[] => selectItems<Shell>(body);

export function useDrafts(session: Session): Resource<FragmentDraft[]> {
  return useResource<FragmentDraft[]>(session, '/fragments', selectDrafts);
}

export function useShells(session: Session): Resource<Shell[]> {
  return useResource<Shell[]>(session, '/shells', selectShells);
}
